interface SkeletonProps {
  className?: string
}

/**
 * A shimmering placeholder shaped like the content it stands in for.
 *
 * The app had 25 centred spinners doing this job. A spinner tells you to wait;
 * a skeleton tells you what you are waiting for, and the page does not jump
 * when the data lands.
 */
export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div
      aria-hidden
      className={`animate-shimmer rounded-sm bg-[linear-gradient(90deg,var(--gym-veil)_25%,var(--gym-wash)_50%,var(--gym-veil)_75%)] bg-[length:200%_100%] ${className}`}
    />
  )
}

/** A stat readout: big number over a label. */
export function SkeletonStat() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-8 w-16" />
      <Skeleton className="h-3 w-20" />
    </div>
  )
}

/** A list row with a leading thumbnail. */
export function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <Skeleton className="h-12 w-12 shrink-0 rounded-sm" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Skeleton className="h-4 w-2/5" />
        <Skeleton className="h-3 w-1/4" />
      </div>
      <Skeleton className="h-6 w-10 shrink-0 rounded-xs" />
    </div>
  )
}

/** A whole card's worth of copy. */
export function SkeletonCard({ lines = 2 }: { lines?: number }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-[var(--gym-line)] bg-content1 p-5 shadow-e1">
      <Skeleton className="h-4 w-1/3" />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={`h-3 ${i === lines - 1 ? 'w-1/2' : 'w-full'}`} />
      ))}
    </div>
  )
}
